import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Heading2, Paragraph, Caption, InputGroup, Input, Button, Checkbox } from '@shopmuchmore/design-system';
import { logger } from '@shopmuchmore/common-utils';
import { CheckCircle2Icon } from 'lucide-react';
import { useSubscribeNewsletter } from '../static.services';
import type { AxiosError } from 'axios';
import type { NewsletterError } from '../static.services';

const newsletterSchema = z.object({
  email: z.email({ error: 'Please enter a valid email address' }),
  consent: z.literal(true, {
    error: 'Please agree to receive marketing emails to subscribe',
  }),
});

type NewsletterFormData = z.infer<typeof newsletterSchema>;

function Newsletter() {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success'>('idle');

  const { mutateAsync: subscribeNewsletter } = useSubscribeNewsletter();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterSchema),
    defaultValues: {
      email: '',
      consent: false as unknown as true,
    },
  });

  const onSubmit = async (data: NewsletterFormData) => {
    try {
      const payload = {
        email: data.email.trim().toLowerCase(),
      };
      
      logger.log('[Newsletter] Submitting form:', payload);
      await subscribeNewsletter(payload);
      
      reset();
      setSubmitStatus('success');
    } catch (err: unknown) {
      const error = err as AxiosError<NewsletterError>;
      logger.error('[Newsletter] Submission error:', error);
      
      const errorMessage = error.response?.data?.message || 'Something went wrong. Please try again.';
      
      // If it's a 400 or 409, we show it on the email field
      setError('email', { type: 'server', message: errorMessage });
    }
  };

  if (submitStatus === 'success') {
    return (
      <section className="w-full bg-gray-100 py-16 md:py-20">
        <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
          {/* Success Icon */}
          <CheckCircle2Icon className='text-primary mx-auto w-15 h-15' />
          <Heading2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4  italic">
            You're on the list!
          </Heading2>
          <Paragraph className="text-gray-600 text-center">
            Thank you for subscribing. Check your inbox for a confirmation email.
          </Paragraph>
          <Button
            onClick={() => setSubmitStatus('idle')}
            variant="link"
          >
            Subscribe another email
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full bg-gray-100 py-16 md:py-20">
      <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
        {/* Heading */}
        <Heading2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4  italic">
          Join the club for deals you'll love!
        </Heading2>

        {/* Description */}
        <Paragraph className="text-gray-600 mb-8 max-w-lg mx-auto text-center">
          Sign up for our newsletter to receive updates on sales, new arrivals, and more, delivered
          straight to your inbox.
        </Paragraph>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="max-w-md mx-auto space-y-3">
          {/* Email Input + Button Row */}
          <InputGroup error={errors.email?.message} >
            <Input
              type="email"
              placeholder="Enter email address"
              aria-label="Email address"
              {...register('email')}
            />
          </InputGroup>

          {/* Consent Checkbox */}
          <InputGroup error={errors.consent?.message} className="text-left">
            <label htmlFor="newsletter-consent" className="flex items-start gap-2 cursor-pointer">
              <Controller
                name="consent"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="newsletter-consent"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    aria-invalid={!!errors.consent}
                    className="mt-0.5"
                  />
                )}
              />
              <span className="text-sm text-gray-700 leading-normal">
                I agree to receive marketing emails and have read the{' '}
                <Link to="/privacy" className="text-primary underline" target="_blank">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>
          </InputGroup>
          <Button type="submit" fullWidth loading={isSubmitting} className="mt-0">
            Subscribe
          </Button>

          {/* GDPR Disclaimer */}
          <Caption className="text-gray-700">
            You can unsubscribe at any time by clicking the link in the footer of our emails.
          </Caption>
        </form>
      </div>
    </section>
  );
}

export default Newsletter;
